import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cake } from 'lucide-react';
import { describeVoucherValue } from '@mlm/shared';
import { Button, Card, CardContent } from '@mlm/ui';
import { claimBirthdayVouchers, getMyBirthdayVouchers } from '../lib/api';

/** A calendar date (`YYYY-MM-DD`) as "Oct 2, 2026", never shifted by the viewer's timezone. */
function calendarDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const CLAIM_ERROR: Record<string, string> = {
  birthday_kyc_required: 'Verify your account (KYC) first.',
  birthday_not_open: 'The claim is not open today.',
  birthday_already_claimed: 'You already claimed it for this birthday.',
  birthday_no_birth_date: 'Add your birth date first.',
  birthday_no_program: 'There is no birthday gift right now.',
};

/**
 * Birthday gift checks (cashier phase 5): what the member can claim and when.
 * Renders nothing while the tenant has no active birthday program. On the
 * dashboard (`compact`) it shows only while a claim is open and unclaimed.
 */
export function BirthdayGiftCard({ label, compact = false }: { label: string; compact?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['my-birthday-vouchers'],
    queryFn: getMyBirthdayVouchers,
    staleTime: 60_000,
  });
  const claim = useMutation({
    mutationFn: claimBirthdayVouchers,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-birthday-vouchers'] });
      void qc.invalidateQueries({ queryKey: ['my-vouchers'] });
    },
  });
  const v = q.data;
  if (!v || v.programs.length === 0) return null;
  if (compact && !v.canClaim) return null;

  const gifts = v.programs.map((p) => `${p.name} (${describeVoucherValue(p)})`).join(', ');
  const claimError = (claim.error as { response?: { data?: { error?: string } } } | null)?.response
    ?.data?.error;
  const claimedAll =
    !!v.window?.open && v.programs.every((p) => v.claimedProgramIds.includes(p.id));

  let message: React.ReactNode;
  let action: React.ReactNode = null;
  if (!v.birthDate) {
    message = v.birthDateEditable ? (
      <>
        Add your birth date to get a birthday {label}.{' '}
        <Link to="/settings?tab=profile" className="font-medium text-primary hover:underline">
          Go to your profile
        </Link>
      </>
    ) : (
      <>Ask the admin to record your birth date to get a birthday {label}.</>
    );
  } else if (claimedAll) {
    message = <>You claimed your birthday {label}. Find it under Ready to use.</>;
  } else if (v.window?.open && v.kycRequired) {
    message = (
      <>
        Happy birthday! Verify your account to claim your {label}.{' '}
        <Link to="/settings?tab=kyc" className="font-medium text-primary hover:underline">
          Verify now
        </Link>
      </>
    );
  } else if (v.canClaim && v.window) {
    message = (
      <>
        Happy birthday! Your {label} is ready to claim
        {v.window.closesOn === v.window.opensOn
          ? ' today.'
          : ` until ${calendarDate(v.window.closesOn)}.`}
      </>
    );
    action = (
      <Button size="sm" disabled={claim.isPending} onClick={() => claim.mutate()}>
        {claim.isPending ? 'Claiming…' : `Claim my ${label}`}
      </Button>
    );
  } else if (v.window) {
    message = (
      <>
        Your next birthday {label} opens on {calendarDate(v.window.opensOn)}
        {v.kycRequired ? ', once your account is verified (KYC).' : '.'}
      </>
    );
  }

  return (
    <Card className={compact ? 'border-primary/40' : undefined}>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <Cake className="h-8 w-8 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-semibold">Birthday {label}</p>
          <p className="text-sm text-muted-foreground">{message}</p>
          {!compact && <p className="text-xs text-muted-foreground">{gifts}</p>}
          {claim.isSuccess && (
            <p className="text-sm text-success">
              Claimed. It is in your {label}s, ready to show at the counter.
            </p>
          )}
          {claimError && (
            <p className="text-sm text-destructive">{CLAIM_ERROR[claimError] ?? claimError}</p>
          )}
        </div>
        {action}
      </CardContent>
    </Card>
  );
}
